import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { get, put } from 'aws-amplify/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../styles/management.css';

function EditInvitationScreen() {
  const { invitationId } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    event_id: '',
    eventName: '',
    guestId: '',
    guestName: '',
    status: '',
    notes: '',
    numberOfGuests: 1,
    sendInvitation: []
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvitation();
  }, [invitationId]);

  async function fetchInvitation() {
    try {
      const response = await get('api', `/invitations/${invitationId}`);
      setFormData(response);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching invitation:', err);
      setError('Failed to load invitation');
      setLoading(false);
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const updatedData = {
        ...formData,
        guests: formData.guests.map(guest => {
          const rsvp = formData.rsvps?.find(r => r.guest_id === guest.id) || {};
          return {
            ...guest,
            rsvp: rsvp.rsvp || null,
            notes: rsvp.notes || '',
            dietaryRestrictions: rsvp.dietaryRestrictions || '',
            additionalOptions: rsvp.additionalOptions || false
          };
        })
      };
      
      await put('api', `/invitations/${invitationId}`, {
        body: updatedData
      });
      navigate(`/invitations/${invitationId}`);
    } catch (err) {
      console.error('Error updating invitation:', err);
      setError('Failed to update invitation');
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading invitation details..." />;
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="form-container">
      <h2>Edit Invitation</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="guestName">Guest Name</label>
          <input
            type="text"
            id="guestName"
            name="guestName"
            value={formData.guestName}
            onChange={handleInputChange}
            disabled
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="eventName">Event Name</label>
          <input
            type="text"
            name="eventName"
            value={formData.eventName}
            onChange={handleInputChange}
            disabled
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">Password Protection (Optional)</label>
          <input
            type="password"
            name="password"
            id="password"
            value={formData.password}
            onChange={handleInputChange}
            className="form-input"
            placeholder="Leave empty for no password"
          />
        </div>

        <div className="form-group">
          <label htmlFor="sendInvitation">Contact Methods</label>
          <div className="contact-methods">
            {formData.sendInvitation?.map((contact, index) => (
              <div key={index} className="contact-method">
                <select
                  name={`contactType-${index}`}
                  value={contact.type}
                  onChange={(e) => {
                    const newMethods = [...formData.sendInvitation];
                    newMethods[index] = { ...contact, type: e.target.value };
                    setFormData(prev => ({ ...prev, sendInvitation: newMethods }));
                  }}
                  className="form-select"
                >
                  <option value="EMAIL">Email</option>
                  <option value="SMS">SMS</option>
                </select>
                <input
                  type={contact.type === 'EMAIL' ? 'email' : 'tel'}
                  value={contact.value}
                  onChange={(e) => {
                    const newMethods = [...formData.sendInvitation];
                    newMethods[index] = { ...contact, value: e.target.value };
                    setFormData(prev => ({ ...prev, sendInvitation: newMethods }));
                  }}
                  className="form-input"
                />
                <button
                  type="button"
                  onClick={() => {
                    const newMethods = formData.sendInvitation.filter((_, i) => i !== index);
                    setFormData(prev => ({ ...prev, sendInvitation: newMethods }));
                  }}
                  className="button button--danger"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  sendInvitation: [...(prev.sendInvitation || []), { type: 'EMAIL', value: '', guestId: formData.guestId }]
                }));
              }}
              className="button button--secondary"
            >
              Add Contact Method
            </button>
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="status">Status</label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            required
            className="form-select"
          >
            <option value="PENDING">Pending</option>
            <option value="SENT">Sent</option>
            <option value="ACCEPTED">Accepted</option>
            <option value="DECLINED">Declined</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="numberOfGuests">Number of Guests</label>
          <input
            type="number"
            id="numberOfGuests"
            name="numberOfGuests"
            value={formData.numberOfGuests}
            onChange={handleInputChange}
            min="1"
            required
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes || ''}
            onChange={handleInputChange}
            rows={3}
            className="form-textarea"
          />
        </div>

        <div className="form-actions">
          <button type="submit" className="button button--primary">
            Save Changes
          </button>
          <button
            type="button"
            className="button button--secondary"
            onClick={() => navigate(`/invitations/${invitationId}`)}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditInvitationScreen;